import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BRAND = { primary: [47, 61, 142], secondary: [93, 165, 216], accent: [76, 140, 59], dark: [26, 36, 86] };
const FOOTER_1 = 'Analytica, Avenida Naciones Unidas y Núñez de Vela, Edificio Metropolitan 3er piso';
const FOOTER_2 = 'www.analytica-ec.com  -  fono: +593 991 466 821  -  Quito, Ecuador';

const sanitize = (s) => (s == null ? '' : String(s).replace(/[\u0000-\u001F\u007F-\u009F]/g, ''));
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-EC', { year: 'numeric', month: 'long', day: 'numeric' });
};
const STATUS_LABEL = {
  passed: 'APROBADO', failed: 'FALLIDO', blocked: 'BLOQUEADO',
  pending: 'PENDIENTE', in_progress: 'EN CURSO',
};

const drawHeader = (doc, planNumber, okrCode) => {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, w, 14, 'F');
  doc.setFillColor(...BRAND.secondary);
  doc.rect(0, 14, w, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('ANALYTICA', 12, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Acta de Aceptacion QA · ${okrCode || ''}`, 12, 12.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(planNumber || '', w - 12, 9, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Documento firmado electronicamente', w - 12, 12.5, { align: 'right' });
};

const drawFooter = (doc) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...BRAND.primary);
  doc.setLineWidth(0.4);
  doc.line(12, h - 16, w - 12, h - 16);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(80, 80, 80);
  doc.text(FOOTER_1, w / 2, h - 11, { align: 'center' });
  doc.text(FOOTER_2, w / 2, h - 7.5, { align: 'center' });
  const cur = doc.internal.getCurrentPageInfo().pageNumber;
  const total = doc.internal.getNumberOfPages();
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(`Pagina ${cur} de ${total}`, w - 12, h - 4, { align: 'right' });
};

const sectionTitle = (doc, text, y) => {
  doc.setFillColor(...BRAND.primary);
  doc.rect(12, y - 5, 4, 6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND.primary);
  doc.text(sanitize(text), 19, y);
  doc.setDrawColor(...BRAND.secondary);
  doc.setLineWidth(0.3);
  doc.line(19, y + 1.5, 80, y + 1.5);
  return y + 7;
};

const ensureSpace = (doc, y, needed, planNumber, okrCode) => {
  const h = doc.internal.pageSize.getHeight();
  if (y + needed > h - 22) {
    doc.addPage();
    drawHeader(doc, planNumber, okrCode);
    return 26;
  }
  return y;
};

/** Detect if evidence is a PDF (Cloudinary raw, mime, filename). */
const _isPdfEv = (ev) => {
  if (ev?.content_type === 'application/pdf' || ev?.resource_type === 'raw') return true;
  if (ev?.secure_url && /\.pdf($|\?)/i.test(ev.secure_url)) return true;
  if (ev?.data_url && ev.data_url.startsWith('data:application/pdf')) return true;
  if (ev?.filename && /\.pdf$/i.test(ev.filename)) return true;
  return false;
};
const _evUrl = (ev) => ev?.secure_url || ev?.data_url || '';

/** Returns a viewable PDF URL.
 * Cloudinary URLs are returned as-is. The previous attempt to inject
 * `fl_attachment:false` was incorrect (Cloudinary parsed `false` as the
 * filename, breaking the URL with HTTP 401 / ERR_INVALID_RESPONSE).
 * PDFs uploaded with resource_type=image now serve inline by default. */
const _viewablePdfUrl = (url) => url || '';

const _imgFmt = (src) => {
  const m = src.match(/^data:image\/(png|jpeg|jpg|webp)/i) || src.match(/\.(png|jpe?g|webp)($|\?)/i);
  return m ? m[1].toUpperCase().replace('JPG', 'JPEG') : 'PNG';
};

/**
 * Fetch a remote image (Cloudinary URL) and convert to data URL so jsPDF can embed it.
 * Returns null on failure (will fall back to placeholder).
 */
const _fetchAsDataUrl = async (url) => {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
};

/**
 * Render a grid of evidence (2 cols). Images embedded; PDFs shown as link card.
 */
const drawEvidenceGrid = async (doc, evList, y, w, planNumber, okrCode) => {
  const imgW = (w - 28) / 2 - 3;
  const imgH = 38;
  let col = 0;
  for (const ev of evList) {
    y = ensureSpace(doc, y, imgH + 8, planNumber, okrCode);
    const x = 14 + col * (imgW + 4);
    const url = _evUrl(ev);
    if (_isPdfEv(ev)) {
      // Render as red card with a clickable PDF link
      doc.setDrawColor(220, 60, 60); doc.setFillColor(254, 242, 242);
      doc.roundedRect(x, y, imgW, imgH, 2, 2, 'FD');
      doc.setFontSize(11); doc.setTextColor(160, 30, 30); doc.setFont('helvetica', 'bold');
      doc.text('PDF', x + imgW / 2, y + imgH / 2 - 2, { align: 'center' });
      doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 30, 30);
      const fnLines = doc.splitTextToSize(sanitize(ev.filename || 'evidencia.pdf'), imgW - 4);
      doc.text(fnLines.slice(0, 2), x + imgW / 2, y + imgH / 2 + 4, { align: 'center' });
      const pdfUrl = _viewablePdfUrl(url);
      if (pdfUrl) doc.link(x, y, imgW, imgH, { url: pdfUrl });
    } else if (url) {
      let dataUrl = url.startsWith('data:') ? url : await _fetchAsDataUrl(url);
      if (dataUrl) {
        try {
          doc.addImage(dataUrl, _imgFmt(dataUrl), x, y, imgW, imgH, undefined, 'FAST');
          if (!url.startsWith('data:')) doc.link(x, y, imgW, imgH, { url });
        } catch { /* fall through to placeholder below */ dataUrl = null; }
      }
      if (!dataUrl) {
        doc.setDrawColor(200, 200, 200);
        doc.rect(x, y, imgW, imgH);
        doc.setFontSize(7); doc.setTextColor(150, 150, 150);
        doc.text('(no se pudo cargar)', x + imgW / 2, y + imgH / 2, { align: 'center' });
      }
    } else {
      doc.setDrawColor(200, 200, 200);
      doc.rect(x, y, imgW, imgH);
      doc.setFontSize(7); doc.setTextColor(150, 150, 150);
      doc.text('(sin contenido)', x + imgW / 2, y + imgH / 2, { align: 'center' });
    }
    doc.setFontSize(7); doc.setTextColor(80, 80, 80);
    const cap = sanitize(
      `${ev.filename || ''}${ev._mini ? ' · ' + ev._mini : ''}${ev.note ? ' · ' + ev.note : ''}`
    ).substring(0, 70);
    doc.text(cap, x, y + imgH + 4);
    col++;
    if (col === 2) { col = 0; y += imgH + 8; }
  }
  if (col !== 0) y += imgH + 8;
  return y;
};

/**
 * Generate the Acta PDF.
 * @param {Object} okr - OKR object (with code, objective, key_results, signatures, etc.)
 * @param {Object} plan - Plan info (plan_number, name, client_company_name, contract_number)
 * @param {Array} backlogs - List of backlogs of this OKR (each with checklist, comments, evidence, status)
 * @param {Object} opts - { download: true, filename }
 */
export async function generateActaPDF(okr, plan, backlogs, opts = {}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth();
  const okrCode = okr?.code || '';
  const planNumber = plan?.plan_number || '';

  drawHeader(doc, planNumber, okrCode);
  let y = 26;

  // Cover
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...BRAND.dark);
  doc.text('Acta de Aceptacion de Pruebas', 12, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`Fecha emision: ${fmtDate(new Date().toISOString())}`, 12, y + 13);
  doc.text(`Plan: ${planNumber}${plan?.name ? ' - ' + plan.name : ''}`, 12, y + 18);
  y += 28;

  // Client / Contract block
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(12, y, w - 24, 22, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.primary);
  doc.text('Cliente:', 16, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  doc.text(sanitize(plan?.client_company_name || ''), 35, y + 6);
  if (plan?.contract_number) doc.text(`Contrato: ${plan.contract_number}`, 16, y + 12);
  doc.text(`OKR: ${okrCode}`, 16, y + 17);
  y += 28;

  // Objetivo
  y = sectionTitle(doc, '1. Objetivo y Resultados Clave', y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  const objLines = doc.splitTextToSize(sanitize(okr?.objective || ''), w - 24);
  doc.text(objLines, 12, y);
  y += objLines.length * 5 + 2;
  if (okr?.description) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80, 80, 80);
    const desc = doc.splitTextToSize(sanitize(okr.description), w - 24);
    doc.text(desc, 12, y);
    y += desc.length * 4 + 2;
  }
  if (okr?.key_results && okr.key_results.length > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    okr.key_results.forEach((kr) => {
      const lines = doc.splitTextToSize(sanitize('• ' + kr), w - 30);
      y = ensureSpace(doc, y, lines.length * 4 + 2, planNumber, okrCode);
      doc.text(lines, 16, y);
      y += lines.length * 4 + 1;
    });
  }
  y += 4;

  // Resumen ejecutivo del OKR (basado en backlogs)
  const total = backlogs.length;
  const passed = backlogs.filter(b => b.status === 'passed').length;
  const failed = backlogs.filter(b => b.status === 'failed').length;
  const blocked = backlogs.filter(b => b.status === 'blocked').length;
  const pending = backlogs.filter(b => !['passed', 'failed', 'blocked'].includes(b.status)).length;
  const passPct = total > 0 ? (passed / total * 100).toFixed(1) : 0;

  // Tareas (checklist items) aggregates
  let tasksTotal = 0;
  let tasksDone = 0;
  for (const b of backlogs) {
    for (const ci of (b.checklist || [])) {
      tasksTotal += 1;
      if (ci.done) tasksDone += 1;
    }
  }
  const tasksPct = tasksTotal > 0 ? (tasksDone / tasksTotal * 100).toFixed(1) : 0;
  // Overall avance combinado: 60% backlog status + 40% tareas
  const overallPct = total > 0 || tasksTotal > 0
    ? ((passed + tasksDone) / Math.max(1, total + tasksTotal) * 100).toFixed(1)
    : 0;

  y = ensureSpace(doc, y, 60, planNumber, okrCode);
  y = sectionTitle(doc, '2. Resumen Ejecutivo', y);
  const sumText = doc.splitTextToSize(sanitize(
    `Se trabajaron ${total} backlogs asociados al OKR ${okrCode}, con un total de ${tasksTotal} tareas ` +
    `desglosadas. ${passed} backlogs resultaron APROBADOS (${passPct}% de cobertura exitosa), ` +
    `${failed} reportaron FALLAS, ${blocked} fueron BLOQUEADOS y ${pending} quedaron PENDIENTES. ` +
    `A nivel de tareas individuales, ${tasksDone} de ${tasksTotal} fueron ejecutadas (${tasksPct}%). ` +
    `Mediante la firma de esta acta, el cliente formaliza la aceptacion de los entregables ` +
    `correspondientes al objetivo descrito.`
  ), w - 24);
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text(sumText, 12, y);
  y += sumText.length * 5 + 4;

  // Tarjetas (5 cards)
  const cardW = (w - 28 - 16) / 5;
  const cards = [
    { l: 'Backlogs', v: total, c: BRAND.primary },
    { l: 'Aprobados', v: passed, c: BRAND.accent },
    { l: 'Tareas', v: `${tasksDone}/${tasksTotal}`, c: [124, 58, 237] },
    { l: '% Tareas', v: tasksPct + '%', c: [167, 139, 250] },
    { l: '% Avance', v: overallPct + '%', c: BRAND.secondary },
  ];
  y = ensureSpace(doc, y, 22, planNumber, okrCode);
  cards.forEach((c, i) => {
    const x = 14 + i * (cardW + 3);
    doc.setFillColor(...c.c);
    doc.roundedRect(x, y, cardW, 16, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(String(c.v), x + cardW / 2, y + 8, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(c.l, x + cardW / 2, y + 13, { align: 'center' });
  });
  y += 22;

  // PROGRESS CHART (donut + barra de backlogs por estado)
  y = ensureSpace(doc, y, 55, planNumber, okrCode);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.primary);
  doc.text('Indicadores graficos', 14, y);
  y += 4;
  const chartY = y;
  // -- Donut (overall %) --
  const donutCX = 38;
  const donutCY = chartY + 22;
  const donutR = 18;
  // Background ring (gray)
  doc.setFillColor(229, 231, 235);
  doc.circle(donutCX, donutCY, donutR, 'F');
  // Foreground arc (green) approximated using filled wedge via path
  const pct = Math.max(0, Math.min(100, parseFloat(overallPct)));
  if (pct > 0) {
    // Use jsPDF lines to draw an arc segment (approx with 36 polygons)
    const steps = Math.max(2, Math.round(pct * 0.6));
    doc.setFillColor(...BRAND.accent);
    // Draw filled triangles forming a pie slice
    for (let i = 0; i < steps; i++) {
      const a0 = -Math.PI / 2 + (i / 60) * Math.PI * 2;
      const a1 = -Math.PI / 2 + ((i + 1) / 60) * Math.PI * 2;
      const x1 = donutCX + Math.cos(a0) * donutR;
      const y1 = donutCY + Math.sin(a0) * donutR;
      const x2 = donutCX + Math.cos(a1) * donutR;
      const y2 = donutCY + Math.sin(a1) * donutR;
      doc.triangle(donutCX, donutCY, x1, y1, x2, y2, 'F');
    }
  }
  // Inner white circle to make it a donut
  doc.setFillColor(255, 255, 255);
  doc.circle(donutCX, donutCY, donutR * 0.6, 'F');
  // Center text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.primary);
  doc.text(`${overallPct}%`, donutCX, donutCY + 1, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(100, 100, 100);
  doc.text('Avance', donutCX, donutCY + 5, { align: 'center' });

  // -- Horizontal bar chart (backlogs por estado) --
  const barX = 70;
  const barW = w - barX - 18;
  const barH = 5;
  const barGap = 7;
  const maxVal = Math.max(passed, failed, blocked, pending, 1);
  const items = [
    { l: 'Aprobados', v: passed, c: BRAND.accent },
    { l: 'Fallidos', v: failed, c: [220, 38, 38] },
    { l: 'Bloqueados', v: blocked, c: [180, 83, 9] },
    { l: 'Pendientes', v: pending, c: [156, 163, 175] },
  ];
  doc.setFontSize(8);
  items.forEach((it, i) => {
    const ry = chartY + 5 + i * barGap;
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.text(it.l, barX, ry);
    // bg
    doc.setFillColor(229, 231, 235);
    doc.roundedRect(barX + 22, ry - 3, barW - 22, barH, 1, 1, 'F');
    // fg
    const fillW = (it.v / maxVal) * (barW - 22);
    if (fillW > 0) {
      doc.setFillColor(...it.c);
      doc.roundedRect(barX + 22, ry - 3, fillW, barH, 1, 1, 'F');
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text(String(it.v), barX + barW + 2, ry, { align: 'right' });
  });
  // Tareas progress bar (full width below)
  const tareasY = chartY + 5 + items.length * barGap + 3;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(124, 58, 237);
  doc.text(`Tareas ejecutadas: ${tasksDone}/${tasksTotal}`, barX, tareasY);
  doc.setFillColor(229, 231, 235);
  doc.roundedRect(barX + 42, tareasY - 3, barW - 42, barH, 1, 1, 'F');
  if (tasksTotal > 0) {
    const tFillW = (tasksDone / tasksTotal) * (barW - 42);
    if (tFillW > 0) {
      doc.setFillColor(124, 58, 237);
      doc.roundedRect(barX + 42, tareasY - 3, tFillW, barH, 1, 1, 'F');
    }
  }
  doc.setTextColor(40, 40, 40);
  doc.text(`${tasksPct}%`, barX + barW + 2, tareasY, { align: 'right' });
  y = chartY + 50;

  // Resumen de Backlogs (tabla)
  doc.addPage(); drawHeader(doc, planNumber, okrCode); y = 26;
  y = sectionTitle(doc, '3. Resumen de Backlogs', y);
  autoTable(doc, {
    head: [['Codigo', 'Backlog', 'Requisito', 'Prio', 'Estado', 'Checklist']],
    body: backlogs.map(b => {
      const cl = b.checklist || [];
      const done = cl.filter(c => c.done).length;
      return [
        b.code,
        sanitize((b.title || '').substring(0, 55)),
        sanitize((b.requirement || '').substring(0, 25)),
        (b.priority || '').toUpperCase()[0],
        STATUS_LABEL[b.status] || (b.status || '-').toUpperCase(),
        cl.length > 0 ? `${done}/${cl.length}` : '-',
      ];
    }),
    startY: y,
    theme: 'striped',
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 18, fontStyle: 'bold' },
      3: { halign: 'center', cellWidth: 12 },
      4: { halign: 'center', cellWidth: 22 },
      5: { halign: 'center', cellWidth: 18 },
    },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Detalle de cada Backlog con checklist + comments + evidencias
  doc.addPage(); drawHeader(doc, planNumber, okrCode); y = 26;
  y = sectionTitle(doc, '4. Detalle por Backlog', y);
  for (const b of backlogs) {
    y = ensureSpace(doc, y, 22, planNumber, okrCode);
    doc.setFillColor(243, 246, 250);
    doc.roundedRect(12, y, w - 24, 11, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...BRAND.dark);
    doc.text(`${b.code} · ${sanitize(b.title || '')}`, 15, y + 4.5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
    const meta = [
      `Estado: ${STATUS_LABEL[b.status] || b.status || '-'}`,
      `Prioridad: ${(b.priority || '-').toUpperCase()}`,
      b.due_date ? `Vence: ${b.due_date}` : null,
      b.requirement ? `Req: ${b.requirement}` : null,
    ].filter(Boolean).join('  ·  ');
    doc.text(sanitize(meta), 15, y + 9);
    y += 14;

    if (b.description) {
      const desc = doc.splitTextToSize(sanitize(b.description), w - 24);
      y = ensureSpace(doc, y, desc.length * 4 + 2, planNumber, okrCode);
      doc.setFontSize(9); doc.setTextColor(60, 60, 60);
      doc.text(desc, 15, y);
      y += desc.length * 4 + 2;
    }

    // Checklist (Tareas) — con columna "Evidencias" mostrando links de Cloudinary
    if (b.checklist && b.checklist.length > 0) {
      const rows = b.checklist.map(ci => {
        const statusLabel = ci.done
          ? 'COMPLETADA'
          : (ci.due_date && ci.due_date < new Date().toISOString().slice(0, 10) ? 'VENCIDA' : 'PENDIENTE');
        const evLinks = (ci.evidence || [])
          .map((ev, idx) => {
            const url = ev.secure_url || ev.data_url || '';
            const isPdf = ev.content_type === 'application/pdf' || /\.pdf($|\?)/i.test(ev.filename || '') || /\.pdf($|\?)/i.test(url);
            return { label: `${isPdf ? 'PDF' : 'IMG'}-${idx + 1}`, url, filename: ev.filename };
          })
          .filter(e => e.url);
        return [
          ci.done ? 'OK' : '-',
          sanitize(ci.title || ''),
          sanitize(ci.assigned_to || '-'),
          sanitize((ci.responsible_org === 'client' ? 'Cliente' : 'Analytica')),
          sanitize(ci.due_date || '-'),
          statusLabel,
          { _evLinks: evLinks },
        ];
      });
      y = ensureSpace(doc, y, 18, planNumber, okrCode);
      autoTable(doc, {
        head: [['', 'Tarea', 'Responsable', 'Org', 'Fecha', 'Estado', 'Evidencias']],
        body: rows,
        startY: y,
        theme: 'grid',
        headStyles: { fillColor: BRAND.secondary, textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 7.5 },
        columnStyles: {
          0: { cellWidth: 6, halign: 'center' },
          2: { cellWidth: 22 },
          3: { cellWidth: 16, halign: 'center' },
          4: { cellWidth: 18, halign: 'center' },
          5: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
          6: { cellWidth: 36 },
        },
        didParseCell: (data) => {
          // Status column coloring
          if (data.section === 'body' && data.column.index === 5) {
            const v = data.cell.raw;
            if (v === 'COMPLETADA') {
              data.cell.styles.textColor = [22, 101, 52];
              data.cell.styles.fillColor = [220, 252, 231];
            } else if (v === 'VENCIDA') {
              data.cell.styles.textColor = [153, 27, 27];
              data.cell.styles.fillColor = [254, 226, 226];
            } else {
              data.cell.styles.textColor = [120, 53, 15];
              data.cell.styles.fillColor = [254, 243, 199];
            }
          }
          // Evidencias column: render label list (will be overlaid with clickable links)
          if (data.section === 'body' && data.column.index === 6) {
            const raw = data.cell.raw;
            const evLinks = raw && raw._evLinks ? raw._evLinks : [];
            data.cell.text = evLinks.length === 0
              ? ['—']
              : evLinks.map(e => e.label);
            if (evLinks.length === 0) {
              data.cell.styles.textColor = [180, 180, 180];
            } else {
              data.cell.styles.textColor = [37, 99, 235];
              data.cell.styles.font = 'helvetica';
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
        didDrawCell: (data) => {
          // Add clickable link annotation on Evidencias column
          if (data.section === 'body' && data.column.index === 6) {
            const raw = data.row.raw[6];
            const evLinks = raw && raw._evLinks ? raw._evLinks : [];
            if (!evLinks.length) return;
            const lineH = 4;
            for (let i = 0; i < evLinks.length; i++) {
              const yLine = data.cell.y + 3 + i * lineH;
              if (yLine + lineH > data.cell.y + data.cell.height) break;
              doc.link(data.cell.x + 1, yLine - 3.2, data.cell.width - 2, lineH, { url: evLinks[i].url });
            }
          }
        },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 3;
    }

    // last_note
    if (b.last_note) {
      const noteLines = doc.splitTextToSize(sanitize('Observacion: ' + b.last_note), w - 24);
      y = ensureSpace(doc, y, noteLines.length * 4 + 2, planNumber, okrCode);
      doc.setFontSize(8); doc.setTextColor(110, 110, 110); doc.setFont('helvetica', 'italic');
      doc.text(noteLines, 15, y);
      y += noteLines.length * 4 + 2;
      doc.setFont('helvetica', 'normal');
    }

    // Comentarios
    if (b.comments && b.comments.length > 0) {
      y = ensureSpace(doc, y, 8, planNumber, okrCode);
      doc.setFontSize(8); doc.setTextColor(...BRAND.primary); doc.setFont('helvetica', 'bold');
      doc.text(`Comentarios (${b.comments.length})`, 15, y);
      y += 3;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
      for (const c of b.comments) {
        const head = `${c.author || ''} · ${fmtDate(c.created_at)}`;
        const text = doc.splitTextToSize(sanitize(c.text || ''), w - 30);
        y = ensureSpace(doc, y, (text.length + 1) * 4 + 2, planNumber, okrCode);
        doc.setFontSize(7); doc.setTextColor(...BRAND.primary);
        doc.text(head, 18, y);
        y += 3;
        doc.setFontSize(8); doc.setTextColor(60, 60, 60);
        doc.text(text, 18, y);
        y += text.length * 4 + 1;
      }
    }

    // Evidencias del Backlog — lista de links Cloudinary (sin embed pesado)
    const evList = b.evidence || [];
    if (evList.length > 0) {
      doc.setFontSize(8); doc.setTextColor(...BRAND.primary); doc.setFont('helvetica', 'bold');
      y = ensureSpace(doc, y, 8 + evList.length * 4, planNumber, okrCode);
      doc.text(`Evidencias del backlog (${evList.length})`, 15, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      for (let i = 0; i < evList.length; i++) {
        const ev = evList[i];
        const url = ev.secure_url || ev.data_url || '';
        if (!url) continue;
        const isPdf = ev.content_type === 'application/pdf' || /\.pdf($|\?)/i.test(ev.filename || '') || /\.pdf($|\?)/i.test(url);
        const labelType = isPdf ? '[PDF]' : '[IMG]';
        const label = `${labelType} ${sanitize(ev.filename || `evidencia-${i + 1}`)}`;
        const lines = doc.splitTextToSize(label, w - 30);
        y = ensureSpace(doc, y, lines.length * 4 + 1, planNumber, okrCode);
        doc.setFontSize(7.5); doc.setTextColor(37, 99, 235);
        doc.textWithLink(lines[0], 18, y, { url });
        if (lines.length > 1) {
          for (let j = 1; j < lines.length; j++) {
            y += 4;
            doc.textWithLink(lines[j], 18, y, { url });
          }
        }
        y += 4;
      }
    }
    y += 4;
  }

  // Aceptación y firmas
  y = ensureSpace(doc, y, 80, planNumber, okrCode);
  y = sectionTitle(doc, '5. Aceptacion y Firmas', y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(40, 40, 40);
  const acceptText = doc.splitTextToSize(sanitize(
    `Yo, en representacion de ${plan?.client_company_name || 'el Cliente'}, declaro haber revisado los ${total} ` +
    `backlogs detallados en este documento y manifiesto mi conformidad con los resultados obtenidos. ` +
    `La presente acta da por cumplida la fase de Quality Assurance para el OKR ${okrCode}.`
  ), w - 24);
  doc.text(acceptText, 12, y);
  y += acceptText.length * 5 + 8;

  // Firmas (cliente + Analytica)
  const sigW = 70;
  const sigH = 26;
  const xLeft = 18;
  const xRight = w - 18 - sigW;
  y = ensureSpace(doc, y, sigH + 22, planNumber, okrCode);

  if (okr?.acta_client_signature_data_url && /^data:image\//.test(okr.acta_client_signature_data_url)) {
    try {
      const fmt = (okr.acta_client_signature_data_url.match(/^data:image\/(png|jpeg|jpg)/i) || [, 'PNG'])[1].toUpperCase();
      doc.addImage(okr.acta_client_signature_data_url, fmt === 'JPG' ? 'JPEG' : fmt, xLeft, y, sigW, sigH);
    } catch {}
  }
  doc.setDrawColor(60, 60, 60); doc.setLineWidth(0.4);
  doc.line(xLeft, y + sigH + 2, xLeft + sigW, y + sigH + 2);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...BRAND.dark);
  doc.text(sanitize(okr?.acta_client_name || plan?.client_company_name || 'Cliente'), xLeft, y + sigH + 7);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
  doc.text('Cliente', xLeft, y + sigH + 12);
  if (okr?.acta_client_signed_at) {
    doc.text(`Firmado: ${fmtDate(okr.acta_client_signed_at)}`, xLeft, y + sigH + 17);
  }

  if (okr?.acta_analytica_signature_data_url && /^data:image\//.test(okr.acta_analytica_signature_data_url)) {
    try {
      const fmt = (okr.acta_analytica_signature_data_url.match(/^data:image\/(png|jpeg|jpg)/i) || [, 'PNG'])[1].toUpperCase();
      doc.addImage(okr.acta_analytica_signature_data_url, fmt === 'JPG' ? 'JPEG' : fmt, xRight, y, sigW, sigH);
    } catch {}
  }
  doc.line(xRight, y + sigH + 2, xRight + sigW, y + sigH + 2);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...BRAND.dark);
  doc.text('Alejandro Amaya', xRight, y + sigH + 7);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
  doc.text('CEO ANALYTICA', xRight, y + sigH + 12);
  if (okr?.acta_analytica_signed_at) {
    doc.text(`Firmado: ${fmtDate(okr.acta_analytica_signed_at)}`, xRight, y + sigH + 17);
  }

  // Footer en todas las paginas
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }

  const filename = opts.filename || `Acta_QA_${planNumber}_${okrCode}.pdf`;
  if (opts.download !== false) doc.save(filename);
  return doc;
}
